/**
 * Copy Trading Settings Modal
 *
 * UI for configuring copy trading between master and follower accounts.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  useBrokerStore,
  CopyTradingConfig,
  FollowerConfig,
  CopyDirectionMode,
  CopySizeMode,
  CopyTradeActivity,
} from '../store/brokerStore';
import './CopyTradingSettingsModal.css';

interface CopyTradingSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CopyTradingSettingsModal: React.FC<CopyTradingSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    brokers,
    copyTradingConfig,
    copyTradingEnabled,
    copyTradingHistory,
    copyTradeActivities,
    clearCopyTradeActivities,
    fetchBrokers,
    fetchCopyTradingStatus,
    updateCopyTradingConfig,
    enableCopyTrading,
    disableCopyTrading,
  } = useBrokerStore();

  const [localConfig, setLocalConfig] = useState<CopyTradingConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'history' | 'activity'>('config');

  useEffect(() => {
    if (isOpen) {
      fetchBrokers();
      fetchCopyTradingStatus();
    }
  }, [isOpen, fetchBrokers, fetchCopyTradingStatus]);

  useEffect(() => {
    if (copyTradingConfig) {
      setLocalConfig(copyTradingConfig);
    } else {
      // Initialize default config
      setLocalConfig({
        enabled: false,
        master: {
          brokerId: '',
          accountId: 0,
        },
        followers: [],
      });
    }
  }, [copyTradingConfig]);

  if (!isOpen) return null;

  const connectedBrokers = brokers.filter((b) => b.status === 'connected');
  const MAX_FOLLOWERS = 10;

  const handleMasterChange = (brokerId: string, accountId: number) => {
    if (!localConfig) return;
    setLocalConfig({
      ...localConfig,
      master: { brokerId, accountId },
    });
  };

  const addFollower = () => {
    if (!localConfig) return;
    if (localConfig.followers.length >= MAX_FOLLOWERS) return;

    const newFollower: FollowerConfig = {
      brokerId: '',
      accountId: 0,
      enabled: true,
      directionMode: 'same',
      sizeMode: 'mirror',
      copyBrackets: true,
      copyFlattens: true,
    };

    setLocalConfig({
      ...localConfig,
      followers: [...localConfig.followers, newFollower],
    });
  };

  const removeFollower = (index: number) => {
    if (!localConfig) return;
    setLocalConfig({
      ...localConfig,
      followers: localConfig.followers.filter((_, i) => i !== index),
    });
  };

  const updateFollower = (index: number, updates: Partial<FollowerConfig>) => {
    if (!localConfig) return;
    setLocalConfig({
      ...localConfig,
      followers: localConfig.followers.map((f, i) =>
        i === index ? { ...f, ...updates } : f
      ),
    });
  };

  // Check if we have any hedge followers
  const hasHedgeFollower = localConfig?.followers.some(
    f => f.enabled && f.directionMode === 'inverse'
  );

  // Check if we have any same-direction followers
  const hasSameDirectionFollower = localConfig?.followers.some(
    f => f.enabled && f.directionMode !== 'inverse'
  );

  // Count enabled hedge followers
  const enabledHedgeFollowerCount = localConfig?.followers.filter(
    f => f.enabled && f.directionMode === 'inverse'
  ).length || 0;

  // Helper to check if a follower is the same as master
  const isSameAsMaster = (follower: FollowerConfig) => {
    return localConfig?.master &&
      follower.brokerId === localConfig.master.brokerId &&
      follower.accountId === localConfig.master.accountId;
  };

  const handleSave = async () => {
    if (!localConfig) return;

    // Validate
    if (!localConfig.master.brokerId || !localConfig.master.accountId) {
      setError('Please select a master account');
      return;
    }

    // Validate all configured followers have broker and account selected
    for (let i = 0; i < localConfig.followers.length; i++) {
      const follower = localConfig.followers[i];
      if (!follower.brokerId || !follower.accountId) {
        setError('Please configure all follower accounts or remove unconfigured ones');
        return;
      }

      // Check if follower is same as master
      if (isSameAsMaster(follower)) {
        setError(`Follower ${i + 1}: Cannot use master account as a follower (would create infinite loop)`);
        return;
      }
    }

    // Validate hedge mode restrictions
    const enabledFollowers = localConfig.followers.filter(f => f.enabled);
    const hedgeFollowers = enabledFollowers.filter(f => f.directionMode === 'inverse');
    const sameDirectionFollowers = enabledFollowers.filter(f => f.directionMode !== 'inverse');

    if (hedgeFollowers.length > 1) {
      setError('Hedge mode: Only ONE hedge (inverse) follower is allowed');
      return;
    }

    if (hedgeFollowers.length > 0 && sameDirectionFollowers.length > 0) {
      setError('Hedge mode: Cannot mix hedge (inverse) with same-direction followers. Disable or remove same-direction followers.');
      return;
    }

    setIsSaving(true);
    setError(null);

    // Preserve the current enabled state when saving config
    const configToSave = {
      ...localConfig,
      enabled: copyTradingEnabled,
    };
    const result = await updateCopyTradingConfig(configToSave);
    setIsSaving(false);

    if (!result.success) {
      setError(result.error || 'Failed to save configuration');
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    setError(null);

    const result = copyTradingEnabled
      ? await disableCopyTrading()
      : await enableCopyTrading();

    setIsToggling(false);

    if (!result.success) {
      setError(result.error || 'Failed to toggle copy trading');
    }
  };

  const getBrokerAccounts = (brokerId: string) => {
    const broker = brokers.find((b) => b.id === brokerId);
    // Only return enabled accounts
    return broker?.accounts.filter(a => a.enabled !== false) || [];
  };

  // Use portal to render modal at document body level, escaping any stacking context
  return createPortal(
    <div className="copytrade-modal-overlay" onClick={onClose}>
      <div className="copytrade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="copytrade-modal-header">
          <h2>Copy Trading</h2>
          <div className="copytrade-header-actions">
            <button
              className={`copytrade-toggle ${copyTradingEnabled ? 'active' : ''}`}
              onClick={handleToggle}
              disabled={isToggling || !localConfig?.master.brokerId}
            >
              {isToggling
                ? 'Toggling...'
                : copyTradingEnabled
                  ? 'Enabled'
                  : 'Disabled'}
            </button>
            <button className="copytrade-modal-close" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>

        <div className="copytrade-tabs">
          <button
            className={`copytrade-tab ${activeTab === 'config' ? 'active' : ''}`}
            onClick={() => setActiveTab('config')}
          >
            Configuration
          </button>
          <button
            className={`copytrade-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History ({copyTradingHistory.length})
          </button>
          <button
            className={`copytrade-tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity ({copyTradeActivities.length})
          </button>
        </div>

        <div className="copytrade-modal-content">
          {error && <div className="copytrade-error">{error}</div>}

          {activeTab === 'config' ? (
            <>
              {connectedBrokers.length === 0 ? (
                <div className="copytrade-empty">
                  <p>No brokers connected.</p>
                  <p>Please connect at least two broker accounts to enable copy trading.</p>
                </div>
              ) : (
                <>
                  {/* Master Account Section */}
                  <div className="copytrade-section">
                    <h3>Master Account</h3>
                    <p className="copytrade-section-desc">
                      Orders placed on this account will be copied to followers.
                    </p>
                    <div className="copytrade-account-row">
                      <select
                        value={localConfig?.master.brokerId || ''}
                        onChange={(e) => {
                          const brokerId = e.target.value;
                          const accounts = getBrokerAccounts(brokerId);
                          handleMasterChange(brokerId, accounts[0]?.id || 0);
                        }}
                      >
                        <option value="">Select Broker</option>
                        {connectedBrokers.map((broker) => (
                          <option key={broker.id} value={broker.id}>
                            {broker.name}
                          </option>
                        ))}
                      </select>

                      <select
                        value={localConfig?.master.accountId || ''}
                        onChange={(e) =>
                          handleMasterChange(
                            localConfig?.master.brokerId || '',
                            parseInt(e.target.value)
                          )
                        }
                        disabled={!localConfig?.master.brokerId}
                      >
                        <option value="">Select Account</option>
                        {getBrokerAccounts(localConfig?.master.brokerId || '').map(
                          (account) => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Hedge Mode Info Banner */}
                  {hasHedgeFollower && (
                    <div className="copytrade-hedge-banner">
                      <div className="copytrade-hedge-icon">⚖️</div>
                      <div className="copytrade-hedge-content">
                        <strong>Hedge Mode Active</strong>
                        <p>TP = SL will be enforced on all orders. When one side hits TP/SL, both positions will be flattened automatically.</p>
                      </div>
                    </div>
                  )}

                  {/* Followers Section */}
                  <div className="copytrade-section">
                    <div className="copytrade-section-header">
                      <h3>Follower Accounts ({localConfig?.followers.length || 0}/{MAX_FOLLOWERS})</h3>
                      <button
                        className="copytrade-add-btn"
                        onClick={addFollower}
                        disabled={(localConfig?.followers.length || 0) >= MAX_FOLLOWERS}
                      >
                        + Add Follower
                      </button>
                    </div>
                    <p className="copytrade-section-desc">
                      Configure how orders are copied to each follower account (0-10 followers).
                    </p>

                    {localConfig?.followers.length === 0 ? (
                      <div className="copytrade-no-followers">
                        No follower accounts configured. You can save with 0 followers or click "Add Follower" to add up to 10.
                      </div>
                    ) : (
                      <div className="copytrade-followers">
                        {localConfig?.followers.map((follower, index) => {
                          const isSameAccount = isSameAsMaster(follower);
                          const isHedgeConflict = hasHedgeFollower && follower.enabled && follower.directionMode !== 'inverse' && enabledHedgeFollowerCount > 0;
                          const wouldAddSecondHedge = follower.enabled && follower.directionMode === 'inverse' && enabledHedgeFollowerCount > 1;

                          return (
                          <div key={index} className={`copytrade-follower-card ${isSameAccount ? 'error' : ''} ${follower.directionMode === 'inverse' ? 'hedge' : ''}`}>
                            {/* Same account warning */}
                            {isSameAccount && (
                              <div className="copytrade-follower-warning error">
                                Cannot use master account as follower
                              </div>
                            )}

                            {/* Hedge conflict warning */}
                            {isHedgeConflict && (
                              <div className="copytrade-follower-warning">
                                Cannot mix with hedge follower - disable or switch to inverse
                              </div>
                            )}

                            {/* Multiple hedge warning */}
                            {wouldAddSecondHedge && (
                              <div className="copytrade-follower-warning">
                                Only one hedge follower allowed
                              </div>
                            )}

                            <div className="copytrade-follower-header">
                              <label className="copytrade-follower-enabled">
                                <input
                                  type="checkbox"
                                  checked={follower.enabled}
                                  onChange={(e) =>
                                    updateFollower(index, { enabled: e.target.checked })
                                  }
                                />
                                Enabled
                              </label>
                              <button
                                className="copytrade-remove-btn"
                                onClick={() => removeFollower(index)}
                              >
                                Remove
                              </button>
                            </div>

                            <div className="copytrade-follower-row">
                              <select
                                value={follower.brokerId}
                                onChange={(e) => {
                                  const brokerId = e.target.value;
                                  const accounts = getBrokerAccounts(brokerId);
                                  updateFollower(index, {
                                    brokerId,
                                    accountId: accounts[0]?.id || 0,
                                  });
                                }}
                              >
                                <option value="">Select Broker</option>
                                {connectedBrokers.map((broker) => (
                                  <option key={broker.id} value={broker.id}>
                                    {broker.name}
                                  </option>
                                ))}
                              </select>

                              <select
                                value={follower.accountId}
                                onChange={(e) =>
                                  updateFollower(index, {
                                    accountId: parseInt(e.target.value),
                                  })
                                }
                                disabled={!follower.brokerId}
                              >
                                <option value="">Select Account</option>
                                {getBrokerAccounts(follower.brokerId).map((account) => (
                                  <option key={account.id} value={account.id}>
                                    {account.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="copytrade-follower-options">
                              <div className="copytrade-option">
                                <label>Direction</label>
                                <select
                                  value={follower.directionMode}
                                  onChange={(e) =>
                                    updateFollower(index, {
                                      directionMode: e.target.value as CopyDirectionMode,
                                    })
                                  }
                                >
                                  <option value="same">Same (Copy)</option>
                                  <option value="inverse">Inverse (Hedge)</option>
                                </select>
                              </div>

                              <div className="copytrade-option">
                                <label>Size Mode</label>
                                <select
                                  value={follower.sizeMode}
                                  onChange={(e) =>
                                    updateFollower(index, {
                                      sizeMode: e.target.value as CopySizeMode,
                                    })
                                  }
                                >
                                  <option value="mirror">Mirror (1:1)</option>
                                  <option value="scaled">Scaled</option>
                                  <option value="fixed">Fixed Size</option>
                                </select>
                              </div>

                              {follower.sizeMode === 'scaled' && (
                                <div className="copytrade-option">
                                  <label>Multiplier</label>
                                  <input
                                    type="number"
                                    value={follower.sizeMultiplier || 1}
                                    onChange={(e) =>
                                      updateFollower(index, {
                                        sizeMultiplier: parseFloat(e.target.value),
                                      })
                                    }
                                    step="0.1"
                                    min="0.1"
                                  />
                                </div>
                              )}

                              {follower.sizeMode === 'fixed' && (
                                <div className="copytrade-option">
                                  <label>Fixed Size</label>
                                  <input
                                    type="number"
                                    value={follower.fixedSize || 1}
                                    onChange={(e) =>
                                      updateFollower(index, {
                                        fixedSize: parseInt(e.target.value),
                                      })
                                    }
                                    min="1"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="copytrade-follower-checkboxes">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={follower.copyBrackets}
                                  onChange={(e) =>
                                    updateFollower(index, {
                                      copyBrackets: e.target.checked,
                                    })
                                  }
                                />
                                Copy Brackets (TP/SL)
                              </label>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={follower.copyFlattens}
                                  onChange={(e) =>
                                    updateFollower(index, {
                                      copyFlattens: e.target.checked,
                                    })
                                  }
                                />
                                Copy Flatten Commands
                              </label>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : activeTab === 'history' ? (
            /* History Tab */
            <div className="copytrade-history">
              {copyTradingHistory.length === 0 ? (
                <div className="copytrade-empty">
                  <p>No copy trade history yet.</p>
                </div>
              ) : (
                <div className="copytrade-history-list">
                  {copyTradingHistory.map((event) => (
                    <div key={event.id} className="copytrade-history-item">
                      <div className="copytrade-history-header">
                        <span className={`copytrade-history-type ${event.type}`}>
                          {event.type === 'order' ? 'Order' : 'Flatten'}
                        </span>
                        <span className="copytrade-history-time">
                          {new Date(event.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {event.masterOrder && (
                        <div className="copytrade-history-master">
                          Master: {event.masterOrder.action} {event.masterOrder.quantity}{' '}
                          {event.masterOrder.symbol}
                        </div>
                      )}
                      <div className="copytrade-history-results">
                        {event.followerResults.map((result, i) => (
                          <div
                            key={i}
                            className={`copytrade-history-result ${result.status === 'success' ? 'success' : 'error'}`}
                          >
                            {result.brokerId}: {result.status === 'success' ? 'Success' : result.error}
                            {result.action && ` (${result.action} ${result.quantity})`}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Activity Tab */
            <div className="copytrade-activity-feed">
              <div className="copytrade-activity-header">
                <span className="copytrade-activity-count">{copyTradeActivities.length} events</span>
                <button
                  className="copytrade-activity-clear"
                  onClick={() => clearCopyTradeActivities()}
                  disabled={copyTradeActivities.length === 0}
                >
                  Clear
                </button>
              </div>

              {copyTradeActivities.length === 0 ? (
                <div className="copytrade-activity-empty">
                  No activity yet. Events will appear here when copy trading is active.
                </div>
              ) : (
                <div className="copytrade-activity-list">
                  {copyTradeActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className={`copytrade-activity-item ${activity.level}`}
                    >
                      <div className="copytrade-activity-time">
                        {new Date(activity.ts).toLocaleTimeString()}
                      </div>
                      <div className="copytrade-activity-badge" data-event={activity.event}>
                        {activity.event}
                      </div>
                      <div className="copytrade-activity-msg">{activity.msg}</div>
                      {activity.duration_ms != null && (
                        <div className="copytrade-activity-duration">{activity.duration_ms}ms</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {activeTab === 'config' && (
          <div className="copytrade-modal-footer">
            <button className="copytrade-btn cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="copytrade-btn save"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CopyTradingSettingsModal;
